﻿import { FC, FormEvent, useMemo, useState } from 'react';
import {
  Container,
  Section,
  Card,
  CardTitle,
  CardDescription,
  Alert,
  Button,
  Badge,
} from '../components/ui';
import { SEOHead } from '../components/seo';
import { ROUTES } from '../config/routes';
import { getProductBySlug } from '../config/catalog';
import { apiClient } from '../api/client';
import { appConfig } from '../config/env';
import { PageProps } from './HomePage';

interface CreateOrderResponse {
  orderId: string;
  orderNumber: string;
  razorpayOrderId: string;
  productSlug: string;
  productTitle: string;
  amountINR: number;
  currency: string;
  status: string;
  paymentStatus: string;
}

interface BackendOrderResponse {
  success: boolean;
  data: CreateOrderResponse;
}
interface RazorpayPaymentResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

interface PaymentVerificationResponse {
  success: boolean;
  data: {
    verified: boolean;
    orderId: string;
    orderNumber: string;
    paymentStatus: string;
    downloadAccessGranted: boolean;
    downloadToken?: string;
    fileCount?: number;
  };
}

interface RazorpayCheckoutOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  prefill: {
    name: string;
    email: string;
    contact: string;
  };
  handler: (response: RazorpayPaymentResponse) => void;
  theme: {
    color: string;
  };
}

interface RazorpayInstance {
  open: () => void;
}

interface RazorpayConstructor {
  new (options: RazorpayCheckoutOptions): RazorpayInstance;
}

declare global {
  interface Window {
    Razorpay?: RazorpayConstructor;
  }
}

export const CheckoutPage: FC<PageProps> = ({ onNavigate }) => {
  const productSlug = useMemo(() => {
    return new URLSearchParams(window.location.search).get('product')?.trim() ?? '';
  }, []);

  const product = useMemo(() => {
    return productSlug ? getProductBySlug(productSlug) : undefined;
  }, [productSlug]);

  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [order, setOrder] = useState<CreateOrderResponse | null>(null);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [downloadToken, setDownloadToken] = useState('');

const openRazorpayCheckout = (createdOrder: CreateOrderResponse) => {
  if (!window.Razorpay) {
    setErrorMessage(
      'Razorpay Checkout could not be loaded. Please refresh the page and try again.',
    );
    return;
  }

  const options: RazorpayCheckoutOptions = {
    key: appConfig.payment.razorpayKeyId ?? '',
    amount: createdOrder.amountINR * 100,
    currency: createdOrder.currency,
    name: 'Connect Source',
    description: createdOrder.productTitle,
    order_id: createdOrder.razorpayOrderId,
    prefill: {
      name: customerName.trim(),
      email: customerEmail.trim(),
      contact: customerPhone.trim(),
    },
    handler: async (response) => {
      setErrorMessage('');
      setSubmitting(true);

      try {
        const verificationResult = await apiClient.post<
          {
            razorpay_order_id: string;
            razorpay_payment_id: string;
            razorpay_signature: string;
          },
          PaymentVerificationResponse
        >('/payments/verify', {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });

        if (!verificationResult.success || !verificationResult.data) {
          setErrorMessage(
            verificationResult.error?.message ??
              'Payment verification failed. Please contact Connect Source support.',
          );
          return;
        }

        const verification = verificationResult.data.data;

        if (
          !verification.verified ||
          verification.paymentStatus !== 'paid' ||
          !verification.downloadAccessGranted ||
          !verification.downloadToken
        ) {
          setErrorMessage(
            'Payment was received, but download access could not be confirmed. Please contact Connect Source support.',
          );
          return;
        }

        setDownloadToken(verification.downloadToken);
        setPaymentVerified(true);
        setOrder((currentOrder) =>
          currentOrder
            ? { ...currentOrder, status: 'paid', paymentStatus: 'paid' }
            : currentOrder,
        );
      } catch {
        setErrorMessage(
          'Payment verification could not be completed. Please contact Connect Source support.',
        );
      } finally {
        setSubmitting(false);
      }
    },
    theme: {
      color: '#111827',
    },
  };

  const razorpay = new window.Razorpay(options);
  razorpay.open();
};
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
if (!window.Razorpay) {
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.async = true;

  await new Promise<void>((resolve, reject) => {
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Razorpay Checkout could not be loaded.'));
    document.body.appendChild(script);
  });
}

    if (!product || product.type !== 'paid_product') {
      setErrorMessage('The selected product is not available for purchase.');
      return;
    }

    setErrorMessage('');
    setSubmitting(true);

    const result = await apiClient.post<
      {
        customerName: string;
        customerEmail: string;
        customerPhone?: string;
        productSlug: string;
      },
      BackendOrderResponse
    >('/orders', {
      customerName: customerName.trim(),
      customerEmail: customerEmail.trim(),
      ...(customerPhone.trim()
        ? { customerPhone: customerPhone.trim() }
        : {}),
      productSlug: product.slug,
    });

    setSubmitting(false);

    if (!result.success || !result.data) {
      setErrorMessage(
        result.error?.message ??
          'We could not initialize your order. Please try again.',
      );
      return;
    }

    const createdOrder = result.data.data;

setOrder(createdOrder);
openRazorpayCheckout(createdOrder);
  };

  if (!product || product.type !== 'paid_product') {
    return (
      <>
        <SEOHead
          title="Checkout — Connect Source"
          description="Secure checkout portal for Connect Source career toolkits."
          noIndex={true}
        />

        <Section
          title="Checkout"
          subtitle="The selected product could not be found."
          style={{ padding: '4.5rem 0' }}
        >
          <Container size="md">
            <Card style={{ padding: '2.5rem' }}>
              <Alert variant="error">
                The selected product is unavailable or invalid.
              </Alert>

              <div
                style={{
                  marginTop: '2rem',
                  display: 'flex',
                  gap: '1rem',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="primary"
                  onClick={() => onNavigate(ROUTES.DIGITAL_PRODUCTS)}
                >
                  ← Browse Digital Toolkits
                </Button>

                <Button
                  variant="outline"
                  onClick={() => onNavigate(ROUTES.HOME)}
                >
                  Return to Home
                </Button>
              </div>
            </Card>
          </Container>
        </Section>
      </>
    );
  }

    if (paymentVerified && downloadToken) {
    const apiBaseUrl =
      'https://asia-southeast1-ai-studio-applet-webapp-9afe5.cloudfunctions.net/backend';

    return (
      <>
        <SEOHead
          title="Payment Successful — Connect Source"
          description="Your Connect Source digital toolkit is ready for download."
          noIndex={true}
        />

        <Section
          title="Payment Successful"
          subtitle="Your payment has been verified and your digital toolkit is ready."
          style={{ padding: '4.5rem 0' }}
        >
          <Container size="md">
            <Card style={{ padding: '2.5rem' }}>
              <Badge variant="primary" style={{ marginBottom: '1rem' }}>
                Payment Verified
              </Badge>

              <CardTitle style={{ fontSize: '1.5rem' }}>
                {product.title}
              </CardTitle>

              <CardDescription style={{ marginTop: '0.75rem', lineHeight: 1.65, fontSize: '0.95rem' }}>
                Thank you. Your payment has been verified successfully. Your secure download links are below.
              </CardDescription>

              {order && (
                <div style={{ marginTop: '1.75rem', padding: '1.25rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <p style={{ margin: '0 0 0.5rem' }}><strong>Order Number:</strong> {order.orderNumber}</p>
                  <p style={{ margin: '0 0 0.5rem' }}><strong>Amount:</strong> ₹{order.amountINR.toLocaleString('en-IN')}</p>
                  <p style={{ margin: 0 }}><strong>Payment Status:</strong> Paid</p>
                </div>
              )}

              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ marginBottom: '1rem' }}>Your files</h3>
                <div style={{ display: 'grid', gap: '0.85rem' }}>
                  {product.files.map((file) => (
                    <div key={file.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                      <div>
                        <strong>{file.fileName}</strong>
                        <div style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: '#6b7280' }}>
                          {file.fileFormat.toUpperCase()}
                        </div>
                      </div>
                      <a
                        href={`${apiBaseUrl}/download?token=${encodeURIComponent(downloadToken)}&fileId=${encodeURIComponent(file.id)}`}
                        style={{ display: 'inline-block', padding: '0.7rem 1rem', borderRadius: '6px', background: '#111827', color: '#ffffff', textDecoration: 'none', fontWeight: 600 }}
                      >
                        Download
                      </a>
                    </div>
                  ))}
                </div>
              </div>

              <Alert variant="info" style={{ marginTop: '1.5rem' }}>
                These secure download links expire after the configured access period and are limited to this purchased product.
              </Alert>

              <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <Button variant="primary" onClick={() => onNavigate(ROUTES.DIGITAL_PRODUCTS)}>
                  Browse More Digital Toolkits
                </Button>
                <Button variant="outline" onClick={() => onNavigate(ROUTES.HOME)}>
                  Return to Home
                </Button>
              </div>
            </Card>
          </Container>
        </Section>
      </>
    );
  }

    if (order) {
    return (
      <>
        <SEOHead
          title="Payment — Connect Source"
          description="Complete your secure payment for Connect Source."
          noIndex={true}
        />

        <Section
          title="Complete Your Payment"
          subtitle="Your order has been created. Razorpay Checkout should now be open."
          style={{ padding: '4.5rem 0' }}
        >
          <Container size="md">
            <Card style={{ padding: '2.5rem' }}>
              <Badge
                variant="primary"
                style={{ marginBottom: '1rem' }}
              >
                Payment Pending
              </Badge>

              <CardTitle style={{ fontSize: '1.5rem' }}>
                {order.productTitle}
              </CardTitle>

              <CardDescription
                style={{
                  marginTop: '0.75rem',
                  lineHeight: 1.65,
                  fontSize: '0.95rem',
                }}
              >
                Your order has been created successfully. Complete the
                payment through the Razorpay checkout window to continue.
              </CardDescription>

              <div
                style={{
                  marginTop: '1.75rem',
                  padding: '1.25rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                }}
              >
                <p style={{ margin: '0 0 0.5rem' }}>
                  <strong>Order Number:</strong> {order.orderNumber}
                </p>

                <p style={{ margin: '0 0 0.5rem' }}>
                  <strong>Amount:</strong> ₹
                  {order.amountINR.toLocaleString('en-IN')}
                </p>

                <p style={{ margin: 0 }}>
                  <strong>Payment Status:</strong> {order.paymentStatus}
                </p>
              </div>

              <Alert variant="info" style={{ marginTop: '1.5rem' }}>
                If the Razorpay payment window is not visible, please try
                submitting the checkout form again.
              </Alert>

              <div
                style={{
                  marginTop: '2rem',
                  display: 'flex',
                  gap: '1rem',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="primary"
                  onClick={() => onNavigate(ROUTES.DIGITAL_PRODUCTS)}
                >
                  ← Browse Digital Toolkits
                </Button>

                <Button
                  variant="outline"
                  onClick={() => onNavigate(ROUTES.HOME)}
                >
                  Return to Home
                </Button>
              </div>
            </Card>
          </Container>
        </Section>
      </>
    );
  }
  return (
    <>
      <SEOHead
        title={`Checkout — ${product.title}`}
        description={`Secure checkout for ${product.title}.`}
        noIndex={true}
      />

      <Section
        title="Digital Toolkit Checkout"
        subtitle="Complete your details to initialize your order."
        style={{ padding: '4.5rem 0' }}
      >
        <Container size="md">
          <Card style={{ padding: '2.5rem' }}>
            <Badge
              variant="primary"
              style={{ marginBottom: '1rem' }}
            >
              Secure Order Initialization
            </Badge>

            <CardTitle style={{ fontSize: '1.4rem' }}>
              {product.title}
            </CardTitle>

            <CardDescription
              style={{
                marginTop: '0.5rem',
                lineHeight: 1.65,
              }}
            >
              {product.subtitle}
            </CardDescription>

            <div
              style={{
                marginTop: '1.5rem',
                padding: '1.25rem',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
              }}
            >
              <strong style={{ fontSize: '1.25rem' }}>
                ₹{product.priceINR.toLocaleString('en-IN')}
              </strong>
            </div>

            {errorMessage && (
              <Alert variant="error" style={{ marginTop: '1.5rem' }}>
                {errorMessage}
              </Alert>
            )}

            <form
              onSubmit={handleSubmit}
              style={{
                marginTop: '2rem',
                display: 'grid',
                gap: '1.25rem',
              }}
            >
              <label>
                <span
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                  }}
                >
                  Full Name
                </span>

                <input
                  type="text"
                  value={customerName}
                  onChange={(event) => setCustomerName(event.target.value)}
                  placeholder="Enter your full name"
                  required
                  autoComplete="name"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '0.85rem 1rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem',
                  }}
                />
              </label>

              <label>
                <span
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                  }}
                >
                  Email Address
                </span>

                <input
                  type="email"
                  value={customerEmail}
                  onChange={(event) => setCustomerEmail(event.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '0.85rem 1rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem',
                  }}
                />
              </label>

              <label>
                <span
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: 600,
                  }}
                >
                  Phone / WhatsApp
                </span>

                <input
                  type="tel"
                  value={customerPhone}
                  onChange={(event) => setCustomerPhone(event.target.value)}
                  placeholder="Enter your phone number"
                  autoComplete="tel"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '0.85rem 1rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '1rem',
                  }}
                />
              </label>

              <Alert variant="info">
                Your order will be created as <strong>pending payment</strong>.
                No payment is collected on this step.
              </Alert>

              <div
                style={{
                  display: 'flex',
                  gap: '1rem',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  type="submit"
                  variant="primary"
                  disabled={submitting}
                >
                  {submitting ? 'Creating Order…' : 'Continue to Payment'}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onNavigate(ROUTES.DIGITAL_PRODUCTS)}
                  disabled={submitting}
                >
                  ← Back to Products
                </Button>
              </div>
            </form>
          </Card>
        </Container>
      </Section>
    </>
  );
};